import React, { JSX, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActionType,
    ChannelActionModal,
    ChannelInviteModal,
    ChannelMenu,
    FriendUser,
    useSafePromise,
    sortFriends,
    ChannelSettingModal,
} from "remo-components";
import SideBottomTools from "../components/SideBottomTools";
import {
    ChannelItem,
    channelRoomStore,
    globalStore,
    useChannelRoomStore,
    useChannelStore,
    useFriendStore,
    useGlobalStore,
    usePreferencesStore as getPreferencesStoreHook,
} from "@obsbot/remo-stores";
import { Outlet, useLocation } from "react-router-dom";
import { RouteNameType } from "../route-config";
import { usePushHistory } from "../utils/routes";
import { useTranslate } from "@obsbot/remo-i18n";
import { ListChannelsType, REMO_GAME_WEB_URL, setInviteURLTime } from "@obsbot/remo-server-api";
import { debounce } from "lodash-es";
import { CustomType, ICustomPayload, ICustomPayloadDescription } from "@obsbot/remo-services";
import { eventBus } from "@obsbot/remo-utils";
type ChannelItemType = Pick<ChannelItem, "channelName" | "channelUuid">;
type ChannelEditType = Pick<
    ChannelItem,
    "channelName" | "channelUuid" | "timeInterval" | "maxPlayer"
>;

interface ChannelLayoutWrapperProps {}

const ChannelLayoutWrapper: React.FC<ChannelLayoutWrapperProps> = (): ReactElement => {
    // 查看是否通过快捷键创建频道
    const location = useLocation();

    const pushHistory = usePushHistory();
    const t = useTranslate();
    useEffect(() => {
        const preferenceStore = getPreferencesStoreHook(
            globalStore.userInfo?.user_id ?? "",
        ).getState();
        if (
            (location && location.state?.isShowCreateChannel) ||
            (!useChannelStore.getState().hasChannels &&
                preferenceStore.hasShowedChannelGuide === false &&
                !location.state?.showGuide &&
                !window.location.hash.startsWith("#/channel/room/"))
        ) {
            setIsShowModal(true);
            setCurrentModalType(ActionType.Create);
        }
        if (location && location.state?.roomUUID) {
            const channelStore = useChannelStore.getState();
            const uuid = location.state.roomUUID;
            const channelItem = channelStore.channels.get(uuid);
            const { isJoinedRTC } = useChannelRoomStore.getState();
            if (isJoinedRTC && channelItem) {
                setAskSwitchChannel(channelItem);
                setCurrentModalType(ActionType.Switch);
                setIsShowModal(true);
                return;
            }
            setTimeout(() => {
                eventBus.emit("preview-camera", {
                    isPreview: true,
                    channelItem,
                });
                globalStore.updateToJoinRoomUUID("");
            }, 100);
        }
        if (location.state?.showGuide) {
            globalStore.updateNeedShowGuide({
                channel: true,
                specialEffect: true,
                isToJoined: true,
            });
        }
    }, [location, t, pushHistory]);
    // 我的频道
    const [myChannel, setMyChannel] = useState<ChannelItem[]>([]);
    // 加入的频道
    const [joinedChannel, setJoinedChannel] = useState<ChannelItem[]>([]);
    // 询问切换的频道
    const [askSwitchChannel, setAskSwitchChannel] = useState<ChannelItem | null>(null);
    const sp = useSafePromise();
    const channelStore = useChannelStore();
    const { blockedUsers } = useChannelRoomStore();
    // 是否显示弹出框
    const [isShowModal, setIsShowModal] = useState(false);
    const [createdChannelIndex, setCreatedChannelIndex] = useState<number | null>(null);
    // 当前弹出框类型
    const [currentModalType, setCurrentModalType] = useState<ActionType>(ActionType.Create);
    // 当前正在操作的频道
    const [actionChannel, setActionChannel] = useState<ChannelItem | null>(null);
    // 邀请弹出框
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteUrl, setInviteUrl] = useState("");
    // 邀请的频道
    const [invitedChannel, setInvitedChannel] = useState<string>("");
    // 是否正在请求
    const [isRequesting, setIsRequesting] = useState(false);
    // 显示完整的好友列表
    const { friendList, rtm } = useFriendStore();
    // null 代表不搜索
    const [searchResult, setSearchResult] = useState<FriendUser[] | null>(null);
    // const [messageApi, messageContext] = message.useMessage();
    // 是否发送邀请连接的flag
    const isInviteFlag = useRef<Map<string, boolean>>(new Map());
    // 设置频道弹窗
    const [isSettingChannel, setIsSettingChannel] = useState(false);
    const [channelInfo, setChannelInfo] = useState<ChannelEditType | null>(null);

    const users: FriendUser[] = useMemo(() => {
        if (searchResult) {
            return searchResult;
        }
        const friends = friendList.map(item => ({
            id: item.userID,
            avatar: item.profile.avatar,
            isLastMessageRead: undefined,
            lastMessageTime: undefined,
            status: item.profile.profileCustomField[0]?.value,
            userName: item.profile.nick,
        }));
        const sortedFriends: FriendUser[] = sortFriends(friends).map(item => ({
            id: item.id,
            name: item.userName,
            avatar: item.avatar,
            isOnline: item.status === "online",
        }));
        return sortedFriends;
    }, [friendList, searchResult]);

    const getCreatedChannelIndex = useCallback(
        (createdChannel: ChannelItem[]): void => {
            const myCreatedChannel = createdChannel;
            const defaultName = t("channel.default");
            const regex = new RegExp(`^${defaultName}(\\d+)$`);
            const createdDefaultChannel = myCreatedChannel
                .filter(channel => regex.test(channel.channelName))
                .map(channel => parseInt(channel.channelName.match(regex)![1], 10));
            const maxNumber =
                createdDefaultChannel.length > 0 ? Math.max(...createdDefaultChannel) : 0;
            setCreatedChannelIndex(maxNumber);
        },
        [t],
    );

    const getChannelList = useCallback(async (): Promise<void> => {
        try {
            const uid = useGlobalStore.getState().userInfo?.user_id;
            if (!uid) {
                return;
            }
            const [createdChannel, joinedChannel] = await Promise.all([
                sp(channelStore.listChannels(ListChannelsType.CREATED, uid)),
                sp(channelStore.listChannels(ListChannelsType.JOINED, uid)),
            ]);

            const joinedChannels = joinedChannel.filter(channel => channel.ownerUuid !== uid);
            setMyChannel([...createdChannel]);
            setJoinedChannel([...joinedChannels]);
            const activeChannel = useChannelStore.getState().activeChannel;
            if (activeChannel) {
                let currentActive = createdChannel.find(
                    channel => channel.channelUuid === activeChannel.channelUuid,
                );
                if (!currentActive) {
                    currentActive = joinedChannels.find(
                        channel => channel.channelUuid === activeChannel.channelUuid,
                    );
                }
                channelStore.updateActiveChannel(currentActive || createdChannel[0]);
            } else {
                channelStore.updateActiveChannel(createdChannel[0] || joinedChannel[0]);
            }
            getCreatedChannelIndex(createdChannel);
        } catch (error) {
            console.error("[WEB] 请求Channel列表出错", error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCreatedChannelIndex, sp]);

    useEffect(() => {
        getChannelList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const requestJoined = async (): Promise<void> => {
            const activeChannel = useChannelStore.getState().activeChannel;
            if (!activeChannel) {
                return;
            }
            const uid = useGlobalStore.getState().userInfo?.user_id;
            if (!uid) {
                return;
            }
            if (myChannel.some(channel => channel.ownerUuid === activeChannel.ownerUuid)) {
                return;
            }
            if (joinedChannel.some(channel => channel.channelUuid === activeChannel.channelUuid)) {
                return;
            }
            const newJoined = await channelStore.listChannels(ListChannelsType.JOINED, uid);
            const newMyChannel = await channelStore.listChannels(ListChannelsType.CREATED, uid);
            const joinedChannels = newJoined.filter(channel => channel.ownerUuid !== uid);
            const joinedHasActiveChannel = joinedChannels.some(channel => {
                return channel.channelUuid === activeChannel.channelUuid;
            });
            if (!joinedChannels.length && !newMyChannel.length) {
                useChannelStore.getState().updateActiveChannel(null);
            } else if (joinedHasActiveChannel) {
                const channel = joinedChannels.find(
                    channel => channel.channelUuid === activeChannel.channelUuid,
                );
                channelStore.updateActiveChannel(channel!);
            } else if (!joinedHasActiveChannel) {
                const mineHasActiveChannel = newMyChannel.findIndex(channel => {
                    return channel.channelUuid === activeChannel.channelUuid;
                });
                channelStore.updateActiveChannel(
                    newMyChannel[mineHasActiveChannel === -1 ? 0 : mineHasActiveChannel],
                );
            }
            setMyChannel([...newMyChannel]);
            setJoinedChannel([...joinedChannels]);
        };
        eventBus.on("update-channels", requestJoined);
        return () => {
            eventBus.off("update-channels", requestJoined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 处理选择当前的频道
    const handleChannelChange = useCallback(
        (channelUUID: string): void => {
            const channelroomStore = useChannelRoomStore.getState();
            if (channelroomStore.isJoinedRTC) {
                eventBus.emit("query-channel-room-list", undefined);
                return;
            }
            const idx = [...myChannel, ...joinedChannel].findIndex(
                channel => channel.channelUuid === channelUUID,
            );
            if (idx !== -1) {
                channelStore.updateActiveChannel([...myChannel, ...joinedChannel][idx]);
            }
        },
        [myChannel, joinedChannel],
    );

    // 处理加入房间
    const handleJoinChannel = useCallback(
        (channelItem: ChannelItem, needAsk = true, needPreview = true) => {
            const channelroomStore = useChannelRoomStore.getState();
            if (!channelItem) {
                return;
            }
            if (
                channelroomStore.isJoinedRTC &&
                channelItem.channelUuid === useChannelStore.getState().activeChannel?.channelUuid
            ) {
                return;
            }
            if (channelroomStore.isJoinedRTC && needAsk) {
                setAskSwitchChannel(channelItem);
                setCurrentModalType(ActionType.Switch);
                setIsShowModal(true);
                return;
            }
            if (!channelroomStore.isJoinedRTC && needPreview) {
                eventBus.emit("preview-camera", {
                    isPreview: true,
                    channelItem,
                });
                return;
            }
            channelStore.updateActiveChannel(channelItem);
            pushHistory(RouteNameType.ChannelRoomPage, {
                roomUUID: channelItem?.channelUuid,
            });
        },
        [pushHistory, channelStore],
    );

    // 处理点击创建频道
    const handleCreateChannel = useCallback((): void => {
        getCreatedChannelIndex(myChannel);
        setCurrentModalType(ActionType.Create);
        setIsShowModal(true);
    }, [getCreatedChannelIndex, myChannel]);
    // 处理邀请好友
    const handleInviteMember = useCallback(
        async (channelUuid: string, inviteFlag: string): Promise<void> => {
            const { data: invite_flag } = await setInviteURLTime(inviteFlag, channelUuid);
            const newUrl = `${REMO_GAME_WEB_URL}#/channel/invite/${invite_flag}`;
            setInviteUrl(newUrl);
            setInvitedChannel(channelUuid);
            setShowInviteModal(true);
        },
        [],
    );
    // 处理通知打开邀请好友或频道设置弹窗
    useEffect(() => {
        const handleGuideAction = async ({
            action,
            data,
        }: {
            action: "invite" | "customize";
            data: any;
        }): Promise<void> => {
            if (action === "invite") {
                setInviteUrl(data.url);
                setShowInviteModal(true);
            } else if (action === "customize") {
                const result = await channelStore.queryChannelInfo(data.channelUuid);
                const { channelName, timeInterval, maxPlayer, channelUuid } = result;
                setChannelInfo({
                    channelName,
                    channelUuid,
                    maxPlayer,
                    timeInterval,
                });
                setTimeout(() => {
                    setIsSettingChannel(true);
                }, 0);
            }
        };
        eventBus.on("guide-action", handleGuideAction);
        return () => {
            eventBus.off("guide-action", handleGuideAction);
        };
    }, [channelStore]);
    const handleDoneFn = useCallback(
        async (otherFn: (uid: string) => Promise<void>, updateCreated = false): Promise<void> => {
            const uid = useGlobalStore.getState().userInfo?.user_id;
            if (!uid) {
                return;
            }
            if (isRequesting) {
                return;
            }
            setIsRequesting(true);
            try {
                await otherFn(uid);
                if (updateCreated) {
                    await getChannelList();
                }
            } catch (error) {
                console.error("[WEB] [ERROR] " + otherFn.name + "出错: ", error);
            } finally {
                setIsRequesting(false);
            }
        },
        [isRequesting, getChannelList],
    );
    // 处理创建频道
    const handleCreateChannelDone = useCallback(
        async (_channelId: string, _type: ActionType, channelName: string): Promise<void> => {
            const createChannelFn = async (): Promise<void> => {
                const channelUUID = await sp(
                    channelStore.createChannel({
                        channelName,
                    }),
                );
                const rtcInfo = globalStore.rtcInfo;
                if (!rtcInfo) {
                    setIsShowModal(false);
                    return;
                }
                await rtm?.createRoom({
                    uid: rtcInfo.uid,
                    userSig: rtcInfo.userSig,
                    roomName: channelName,
                    roomUUID: channelUUID,
                });
                setIsShowModal(false);
            };
            handleDoneFn(createChannelFn, true);
        },
        [handleDoneFn, channelStore, sp, rtm],
    );
    // 处理修改频道
    const handleEditChannelDone = useCallback(
        async (channelId: string, _type: ActionType, channelName: string): Promise<void> => {
            const updateChannelFn = async (): Promise<void> => {
                await sp(
                    channelStore.updateChannel({
                        channelName,
                        channelUuid: channelId,
                    }),
                );
                const activeChannel = channelStore.activeChannel;
                if (activeChannel && channelId === activeChannel?.channelUuid) {
                    channelStore.updateActiveChannel({
                        ...activeChannel,
                        channelName,
                    });
                }
                setIsShowModal(false);
            };
            handleDoneFn(updateChannelFn, true);
        },
        [channelStore, handleDoneFn, sp],
    );
    // 处理删除频道
    const handleDeleteChannelDone = useCallback(
        async (channelId: string, actionType: ActionType, _channelName: string): Promise<void> => {
            const channelroomStore = useChannelRoomStore.getState();
            if (
                channelStore.activeChannel?.channelUuid === channelId &&
                channelroomStore.isJoinedRTC
            ) {
                // messageApi.warning(t("channel.no-delete-current-channel"));
                eventBus.emit("show-message", {
                    message: t("channel.no-delete-current-channel"),
                    type: "warning",
                });
                setIsShowModal(false);
                return;
            }
            if (actionType === ActionType.Delete) {
                const updateChannelFn = async (): Promise<void> => {
                    await sp(channelStore.deleteChannel(channelId));
                    setIsShowModal(false);
                };
                handleDoneFn(updateChannelFn, true);
            } else {
                const updateChannelFn = async (): Promise<void> => {
                    const isJoinedRTC = useChannelRoomStore.getState().isJoinedRTC;
                    channelStore.updateActiveChannel(
                        isJoinedRTC ? channelStore.activeChannel : null,
                    );
                    await sp(channelStore.deleteJoinedChannel(channelId));
                    setIsShowModal(false);
                };
                handleDoneFn(updateChannelFn, true);
            }
            const rtcInfo = useGlobalStore.getState().rtcInfo;
            if (!rtcInfo) {
                return;
            }
            // 必须加入群组调用`deleteConversation`才可以删除聊天记录
            await rtm?.joinRoom({
                roomName: "",
                roomUUID: channelId,
                uid: rtcInfo.uid,
                userSig: rtcInfo.userSig,
            });
            await rtm?.deleteConversation("GROUP" + channelId, true);
            await rtm?.leaveRoom();
        },
        [channelStore, handleDoneFn, sp, t, rtm],
    );
    // 处理离开频道
    const handleQuitChannelDone = useCallback(
        (channelId: string, _type: ActionType, _channelName: string): void => {
            channelStore.quitChannel(channelId);
            channelRoomStore.leaveRoom();
            pushHistory(RouteNameType.ChannelPage);
            setIsShowModal(false);
        },
        [channelStore, pushHistory],
    );
    // 处理点击图标修改频道
    const handleEditChannel = useCallback(
        async (channel: ChannelItemType) => {
            const result = await channelStore.queryChannelInfo(channel.channelUuid);
            const { channelName, timeInterval, maxPlayer, channelUuid } = result;
            setChannelInfo({
                channelName,
                channelUuid,
                maxPlayer,
                timeInterval,
            });
            setTimeout(() => {
                setIsSettingChannel(true);
            }, 0);
        },
        [channelStore],
    );
    // 处理右键操作频道
    const handleMenuSelect = useCallback(
        (
            key:
                | "editChannel"
                | "delChannel"
                | "quitChannel"
                | "delJoinChannel"
                | "inviteFriends"
                | "settingChannel",
            channelId: string,
            channelName: string,
        ): void => {
            switch (key) {
                case "editChannel": {
                    setActionChannel(
                        myChannel.find(channel => channel.channelUuid === channelId) || null,
                    );
                    setCurrentModalType(ActionType.Edit);
                    setIsShowModal(true);
                    break;
                }
                case "delChannel": {
                    setActionChannel(
                        myChannel.find(channel => channel.channelUuid === channelId) || null,
                    );
                    setCurrentModalType(ActionType.Delete);
                    setIsShowModal(true);
                    break;
                }
                case "quitChannel": {
                    setActionChannel(
                        joinedChannel.find(channel => channel.channelUuid === channelId) || null,
                    );
                    setCurrentModalType(ActionType.Quit);
                    setIsShowModal(true);
                    break;
                }
                case "delJoinChannel": {
                    setActionChannel(
                        joinedChannel.find(channel => channel.channelUuid === channelId) || null,
                    );
                    setCurrentModalType(ActionType.DeleteJoin);
                    setIsShowModal(true);
                    break;
                }
                case "inviteFriends": {
                    const channel =
                        myChannel.find(channel => channel.channelUuid === channelId) || null;
                    if (channel && channel.inviteFlag) {
                        setInviteUrl(`${REMO_GAME_WEB_URL}#/channel/invite/${channel.inviteFlag}`);
                        setShowInviteModal(true);
                    } else {
                        console.log("[WEB] [ERROR] 频道不存在或链接不存在", channel);
                    }
                    break;
                }
                case "settingChannel": {
                    handleEditChannel({
                        channelUuid: channelId,
                        channelName: channelName,
                    });
                    break;
                }
            }
        },
        [joinedChannel, myChannel, handleEditChannel],
    );
    // 处理渲染频道操作模态窗
    const handleRenderChannelActionModal = useCallback((): JSX.Element | false => {
        switch (currentModalType) {
            case ActionType.Create: {
                return (
                    createdChannelIndex !== null && (
                        <ChannelActionModal
                            actionType={currentModalType}
                            channelId=""
                            channelName={`${t("channel.default")}${createdChannelIndex + 1}`}
                            setVisible={setIsShowModal}
                            visible={isShowModal}
                            onDone={handleCreateChannelDone}
                        />
                    )
                );
            }
            case ActionType.Edit: {
                return (
                    !!actionChannel && (
                        <ChannelActionModal
                            actionType={currentModalType}
                            channelId={actionChannel.channelUuid}
                            channelName={actionChannel.channelName}
                            setVisible={setIsShowModal}
                            visible={isShowModal}
                            onDone={handleEditChannelDone}
                        />
                    )
                );
            }
            case ActionType.DeleteJoin:
            case ActionType.DeleteHasMember:
            case ActionType.Delete: {
                const hasMember =
                    (channelStore.channelMembers.get(actionChannel?.channelUuid ?? "")?.length ??
                        0) > 0;
                return (
                    !!actionChannel && (
                        <ChannelActionModal
                            actionType={currentModalType}
                            channelId={actionChannel.channelUuid}
                            channelName={actionChannel.channelName}
                            hasMember={hasMember}
                            setVisible={setIsShowModal}
                            visible={isShowModal}
                            onDone={handleDeleteChannelDone}
                        />
                    )
                );
            }
            case ActionType.Quit: {
                return (
                    !!actionChannel && (
                        <ChannelActionModal
                            actionType={currentModalType}
                            channelId={actionChannel.channelUuid}
                            channelName={actionChannel.channelName}
                            setVisible={setIsShowModal}
                            visible={isShowModal}
                            onDone={handleQuitChannelDone}
                        />
                    )
                );
            }
            case ActionType.Switch: {
                return (
                    !!askSwitchChannel && (
                        <ChannelActionModal
                            actionType={currentModalType}
                            channelId={askSwitchChannel!.channelUuid}
                            channelName={askSwitchChannel!.channelName}
                            setVisible={setIsShowModal}
                            visible={isShowModal}
                            onDone={async () => {
                                if (!channelStore.activeChannel) {
                                    return;
                                }
                                await channelStore.quitChannel(
                                    channelStore.activeChannel.channelUuid,
                                );
                                await channelRoomStore.leaveRoom();
                                eventBus.emit("switch-channel", undefined);
                                handleJoinChannel(askSwitchChannel!, false, false);
                                setIsShowModal(false);
                            }}
                        />
                    )
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        t,
        channelStore,
        currentModalType,
        isShowModal,
        askSwitchChannel,
        handleJoinChannel,
        handleCreateChannelDone,
        createdChannelIndex,
        actionChannel,
        handleEditChannelDone,
        handleDeleteChannelDone,
        handleQuitChannelDone,
    ]);
    // 处理搜索好友
    const onSearch = (searchContent: string): void => {
        if (!searchContent.length) {
            return setSearchResult(null);
        }
        const friends = friendList;
        const filterFriends = friends
            .filter(friend => friend.profile.nick.includes(searchContent))
            .map(item => ({
                id: item.profile.userID,
                name: item.profile.nick,
                avatar: item.profile.avatar,
                isOnline: item.profile.profileCustomField[0]?.value === "online",
            }));
        setSearchResult(filterFriends);
    };
    const handleSearchInput = debounce((searchContent: string): void => {
        onSearch(searchContent);
    }, 500);
    // 处理邀请好友
    const onInvite = debounce(async (user: FriendUser): Promise<void> => {
        if (blockedUsers.includes(user.id)) {
            eventBus.emit("show-message", {
                message: t("chat.input-enter-disabled"),
                type: "error",
            });
            return;
        }
        const channelKeySplit = inviteUrl.split("/");
        const channelKey = channelKeySplit[channelKeySplit.length - 1];
        const { data: invite_flag } = await setInviteURLTime(channelKey, invitedChannel);
        const newURL = inviteUrl.replace(channelKey, invite_flag);
        setInviteUrl(newURL);
        const payload: ICustomPayload = {
            data: newURL,
            description: ICustomPayloadDescription.Link,
        };
        await rtm?.sendPeerMessage(CustomType.Custom, user.id, payload);
        // messageApi.success(t("channel.invite-friend-success"));
        eventBus.emit("show-message", {
            message: t("channel.invite-friend-success"),
            type: "success",
        });
        isInviteFlag.current.set(user.id, true);
    }, 500);
    useEffect(() => {
        const handleConfirmPreviewCamera = (channel: ChannelItem): void => {
            handleJoinChannel(channel, false, false);
        };
        const handleShowCreateChannel = (): void => {
            getCreatedChannelIndex(myChannel);
            setIsShowModal(true);
            setCurrentModalType(ActionType.Create);
        };
        eventBus.on("confirm-preview-camera", handleConfirmPreviewCamera);
        eventBus.on("show-create-channel", handleShowCreateChannel);
        return () => {
            eventBus.off("confirm-preview-camera", handleConfirmPreviewCamera);
            eventBus.off("show-create-channel", handleShowCreateChannel);
        };
    }, []);

    const noCreateJoinedChannel = useMemo(() => {
        return myChannel.filter(
            channel =>
                !joinedChannel.some(
                    joinedChannel => joinedChannel.channelUuid === channel.channelUuid,
                ),
        );
    }, [myChannel, joinedChannel]);
    const noJoinedCreateChannel = useMemo(() => {
        return joinedChannel.filter(
            channel => !myChannel.some(myChannel => myChannel.channelUuid === channel.channelUuid),
        );
    }, [myChannel, joinedChannel]);
    // 处理点击修改频道
    const handleChannelSetting = useCallback(
        (channel: Pick<ChannelItem, "channelName" | "channelUuid">): void => {
            handleEditChannelDone(channel.channelUuid, ActionType.Edit, channel.channelName);
            // setActionChannel(channel);
            // setCurrentModalType(ActionType.Edit);
            // setIsShowModal(true);
        },
        [handleEditChannelDone],
    );
    return (
        <div className="channel-page-layout">
            <div className="channel-page-side-menu">
                <ChannelMenu
                    activeChannel={useChannelStore.getState().activeChannel}
                    joinedChannels={noJoinedCreateChannel}
                    myChannels={noCreateJoinedChannel}
                    onChangeActiveChannel={handleChannelChange}
                    onChannelSetting={handleChannelSetting}
                    onCreateChannel={handleCreateChannel}
                    onEditChannel={handleEditChannel}
                    onHandleJoinChannel={handleJoinChannel}
                    onInviteMember={handleInviteMember}
                    onMenuSelect={handleMenuSelect}
                />
                <div className="bottom">
                    <SideBottomTools />
                </div>
            </div>
            <Outlet />
            {handleRenderChannelActionModal()}
            <ChannelInviteModal
                isNoFriends={friendList.length === 0}
                setVisible={setShowInviteModal}
                sharelink={inviteUrl}
                users={users}
                visible={showInviteModal}
                onClickAddUser={() => pushHistory(RouteNameType.AddFriendsPage)}
                onCopy={async () => {
                    const channelKeySplit = inviteUrl.split("/");
                    const channelKey = channelKeySplit[channelKeySplit.length - 1];
                    const { data: invite_flag } = await setInviteURLTime(
                        channelKey,
                        invitedChannel,
                    );
                    const newURL = inviteUrl.replace(channelKey, invite_flag);
                    setInviteUrl(newURL);
                }}
                onInput={handleSearchInput}
                onInvite={onInvite}
                onSearch={onSearch}
            />
            {channelInfo && (
                <ChannelSettingModal
                    channelInfo={channelInfo}
                    setVisible={setIsSettingChannel}
                    visible={isSettingChannel}
                    onDone={(editChannelInfo: ChannelEditType) => {
                        handleDoneFn(async () => {
                            await channelStore.updateChannel(editChannelInfo);
                            setIsSettingChannel(false);
                        }, true);
                    }}
                />
            )}
        </div>
    );
};

export default ChannelLayoutWrapper;
